<template>
  <div v-if="opened" class="bg">
    <div class="console">
      <Console
        ref="console"
        v-click-outside="close"
        modal
        @close="close"
        @submit="submit"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, ref, watch } from "vue";

import type { Task } from "@ctrl/controller-rpc";

import assert from "@/assert";
import {
  performAction,
  statePlan,
  statePostpone,
  stateSchedule,
} from "@/components/console";
import * as inject from "@/components/inject";
import {
  CreateObjectQuery,
  State as ParsedInputState,
} from "@/components/parsed-input";
import { Query } from "@/components/ParsedInput.vue";
import { IVar } from "@/ivar";
import { RouteNames } from "@/routes/types";
import { allocateTimeForTask, isBoundTask, planTask } from "@/task";
import type { Router } from "@/types";
import { navigateTo, navigateToObject } from "@/utils/navigate";

import Console from "./Console.vue";

export default defineComponent({
  components: { Console },
  setup() {
    const controller = inject.controller();
    const profile = inject.profile();
    const router = inject.router();
    const console = ref<InstanceType<typeof Console> | null>(null);
    const currentQuery = ref<IVar<CreateObjectQuery | null> | null>(null);
    const opened = ref(false);
    const close = () => {
      assert(currentQuery.value);
      currentQuery.value.fill(null);
      opened.value = false;
    };
    const initConsole = (
      state?: ParsedInputState
    ): IVar<CreateObjectQuery | null> => {
      currentQuery.value = new IVar();
      nextTick(() => {
        console.value!.fill(state || {});
      });
      return currentQuery.value;
    };
    const open = (state?: ParsedInputState): IVar<CreateObjectQuery | null> => {
      opened.value = true;
      return initConsole(state);
    };
    const delay = async (
      task: Task,
      action: "schedule" | "plan" | "postpone",
      router: Router
    ) => {
      const res = await (async () => {
        switch (action) {
          case "schedule":
            return open(stateSchedule(task));
          case "plan":
            return open(statePlan(task));
          case "postpone":
            return open(statePostpone(task));
        }
      })();
      if (res) {
        const { action } = res;
        switch (action.type) {
          case "task-allocation":
            await allocateTimeForTask(
              controller,
              profile.value.user.accountId,
              task,
              action,
              "console"
            );
            break;
          case "task-scheduling":
            await planTask(controller, task.id, action.date, "console");
            break;
          case "task-postponing":
            await planTask(controller, task.id, action.batch, "console");
            break;
          default:
            assert(
              false,
              `a console ${action} yielded a ${action.type} action`
            );
        }
        if (res.options?.isOpeningItem)
          await navigateToObject(router, controller, {
            // A bound task is a database row (`object:…` id): open it as an
            // object.
            kind: isBoundTask(task) ? "object" : "task",
            id: task.id,
          });
      }
    };
    const postpone = async (task: Task, router: Router) => {
      await delay(task, "postpone", router);
    };
    const schedule = async (task: Task, router: Router) => {
      await delay(task, "schedule", router);
    };
    const plan = async (task: Task, router: Router) => {
      await delay(task, "plan", router);
    };
    const submit = async (pl: Query) => {
      if (pl.kind === "create") {
        currentQuery.value!.fill(pl);
        if (pl.options?.isClosingConsole) close();
        else {
          initConsole();
          const res = await currentQuery.value;
          if (res) {
            const id = await performAction(res.action, res.title, {
              controller,
              profile,
              properties: res.properties,
              defaults: res.defaults,
              destination: res.query?.destination,
              labels: res.query?.labels,
            });
            if (res.options?.isOpeningItem) {
              if (typeof id === "string") {
                if (res.action.type === "custom-type")
                  await navigateTo(RouteNames.Object, router, {
                    params: {
                      id,
                      database: res.action.database.name,
                    },
                  });
              } else await navigateToObject(router, controller, id);
            }
          } else close();
        }
      }
    };
    const toggle = async (): Promise<CreateObjectQuery | null> => {
      if (opened.value) {
        close();
        return null;
      } else return open();
    };
    watch(console, (console) => {
      if (console) console.focus();
    });
    return {
      close,
      console,
      open,
      opened,
      postpone,
      schedule,
      plan,
      submit,
      toggle,
    };
  },
});
</script>

<style scoped lang="scss">
.bg {
  position: fixed;
  z-index: $z-index-modal-low;
  top: 0px;
  inset-inline-start: 0px;
  width: 100%;
  height: 100vh;
  @include themed() {
    background: t($bg-overlay);
  }

  .console {
    position: absolute;
    top: 15%;
    inset-inline-start: 50%;
    transform: translate(-50%, 0%);
    width: 830px;
  }
}
</style>
